import { MARKETING_LANGUAGES, marketingLanguagePrefix, isMarketingHomePath, getMarketingLanguageFromPath } from "../../marketing-content/marketingLocales";
// Tells marketing-site URLs (jungleai.com: landing page, blog, use-cases, pricing, …) apart from app URLs.
// This module is part of the app's startup bundle, so it stays tiny: it only knows the list of paths.
// Everything that renders those pages is loaded lazily by MarketingSiteRouter.
import marketingRoutePaths from "../../marketing-content/routePaths.json";
import {
  isMarketingSiteOrigin,
  isProductionMarketingSiteHostname,
  JUNGLE_APP_ORIGIN,
} from "../landing-page/handoff/appHandoff";

// Preview of the landing page designs, available on every origin.
const LANDING_PAGE_PREVIEW_PATH = "/testing";

// Anything under these prefixes belongs to the marketing site even when no page matches, so a mistyped
// blog URL gets the marketing "page not found" rather than the app's.
const MARKETING_SECTION_PATH_PREFIXES = MARKETING_LANGUAGES.flatMap(language => ["blog", "use-cases", "conversions", "alternatives"].map(section => `${marketingLanguagePrefix(language)}/${section}/`));

// A few Framer URLs contain capitals (e.g. /ninja-nerd-Genitourinary-Medicine-…) and react-router matches
// paths case-insensitively, so the comparison is done in lower case.
const lowerCaseMarketingRoutePaths = new Set(
  (marketingRoutePaths as string[]).map((routePath) => routePath.toLowerCase())
);

function normalizePathname(pathname: string): string {
  const pathnameWithoutTrailingSlash = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return pathnameWithoutTrailingSlash.toLowerCase();
}

/** True when `pathname` is rendered by the marketing site rather than the app. */
export function isMarketingSitePath(pathname: string): boolean {
  const normalizedPathname = normalizePathname(pathname);
  if (normalizedPathname === LANDING_PAGE_PREVIEW_PATH) return true;
  // The landing page owns "/" and "/es" only on the marketing domain; on the app's domain "/" is the app's home.
  if (isMarketingHomePath(normalizedPathname)) return isMarketingSiteOrigin();
  if (lowerCaseMarketingRoutePaths.has(normalizedPathname)) return true;
  if (getMarketingLanguageFromPath(normalizedPathname) !== "en") return true;
  return MARKETING_SECTION_PATH_PREFIXES.some((sectionPrefix) => normalizedPathname.startsWith(sectionPrefix));
}

/**
 * On jungleai.com only marketing pages exist. Any other path (an old /login bookmark, a deck link typed
 * without the `app.` subdomain) belongs to the app, which must run on its own origin: sign-in state and
 * Firebase's authorized domains are tied to app.jungleai.com. Returns the URL to send the visitor to, or
 * null when no redirect is needed. Only the real marketing hostnames redirect: the local
 * `jungleai.localhost` alias and preview deployments keep every route reachable.
 */
export function getAppRedirectUrlForNonMarketingPath(
  currentLocation: Pick<Location, "hostname" | "pathname" | "search" | "hash">
): string | null {
  if (!isProductionMarketingSiteHostname(currentLocation.hostname)) return null;
  if (isMarketingSitePath(currentLocation.pathname)) return null;
  return `${JUNGLE_APP_ORIGIN}${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}`;
}

// The build writes a static copy of every marketing page into #root for crawlers and for a fast first
// paint (one rendering per landing variant; the page's inline scripts put the visitor's on the page).
// marketing-entry.tsx hydrates that markup, so it normally stays on screen; the copy captured here
// (module evaluation happens before React's first commit) is the loading fallback for the case where
// React cannot adopt it and for the page's lazily loaded chunks. It is only ever shown for the URL it
// was served for: after an in-app navigation it would be the previous page.
const PRERENDERED_ROOT_ATTRIBUTE = "data-prerendered-variant";
const prerenderedMarketingPage: { pathname: string; html: string } | null =
  typeof document !== "undefined" && document.getElementById("root")?.hasAttribute(PRERENDERED_ROOT_ATTRIBUTE)
    ? { pathname: normalizePathname(window.location.pathname), html: document.getElementById("root")?.innerHTML ?? "" }
    : null;

export function getPrerenderedMarketingPageHtml(): string | null {
  if (!prerenderedMarketingPage || typeof window === "undefined") return null;
  return normalizePathname(window.location.pathname) === prerenderedMarketingPage.pathname
    ? prerenderedMarketingPage.html
    : null;
}

/** True when #root holds the build-time rendering of the current URL, which React can adopt rather than replace. */
export function hasPrerenderedMarketingPageForCurrentUrl(): boolean {
  return getPrerenderedMarketingPageHtml() !== null;
}
